// Formulario web público (§ 1.21, T-119). Sin login. Crea un lead vía POST /f/:token.
import type { JSX, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MODULES } from "../modules/registry.tsx";
import { Button, Card, Field, Input, Loading } from "../components/ui.tsx";

interface Meta { name: string; fields: string[]; thankYouMessage: string; }

const leadFieldLabel = (api: string): string =>
  MODULES["leads"]?.fields.find((f) => f.name === api)?.label ?? api;

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(String(r.status));
  return r.json() as Promise<T>;
}

export function PublicForm(): JSX.Element {
  const { token } = useParams();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "done" | "error">("loading");
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [thankYou, setThankYou] = useState("");

  useEffect(() => {
    jget<Meta>(`/f/${token}/meta`)
      .then((m) => { setMeta(m); setState("ready"); })
      .catch(() => setState("notfound"));
  }, [token]);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!values["lastName"] || !values["company"]) return;
    setBusy(true);
    fetch(`/f/${token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(values),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        const data = (await r.json()) as { message?: string };
        setThankYou(data.message ?? meta?.thankYouMessage ?? "¡Gracias! Nos pondremos en contacto pronto.");
        setState("done");
      })
      .catch(() => setState("error"))
      .finally(() => setBusy(false));
  };

  if (state === "loading") return <div className="min-h-screen grid place-items-center"><Loading /></div>;
  if (state === "notfound") {
    return <Centered><p className="text-ink-secondary">Este formulario no existe o no está disponible.</p></Centered>;
  }
  if (state === "done") {
    return <Centered><div className="text-center"><div className="text-4xl mb-3">✅</div><p className="text-ink-secondary">{thankYou}</p></div></Centered>;
  }

  const fields = meta?.fields ?? ["firstName", "lastName", "company", "email", "phone"];

  return (
    <Centered>
      <h1 className="text-lg font-bold text-ink mb-1">{meta?.name ?? "Contáctanos"}</h1>
      <p className="text-sm text-ink-secondary mb-5">Completa el formulario y te contactaremos.</p>
      {state === "error" && <p className="text-sm text-red-600 mb-3">No se pudo enviar. Intenta de nuevo.</p>}
      <form onSubmit={submit} className="space-y-4">
        {fields.map((f) => {
          const required = f === "lastName" || f === "company";
          return (
            <Field key={f} label={leadFieldLabel(f)} required={required}>
              <Input value={values[f] ?? ""} required={required} onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))} />
            </Field>
          );
        })}
        <Button type="submit" variant="primary" className="w-full" disabled={busy}>{busy ? "Enviando…" : "Enviar"}</Button>
      </form>
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-primary-50 to-white p-4">
      <Card className="w-full max-w-md p-8">{children}</Card>
    </div>
  );
}
